import {
  autoUpdate,
  flip,
  offset,
  type Placement,
  shift,
  useFloating,
} from '@floating-ui/react';
import React, {useLayoutEffect} from 'react';
import {createPortal} from 'react-dom';

export type TooltipAnchor = {
  x: number;
  y: number;
  placement?: 'top' | 'right';
};

// Keep the tooltip a few px away from the viewport edge.
const VIEWPORT_MARGIN = 12;
// Gap between the anchor point and the tooltip.
const ANCHOR_OFFSET = 12;

export const ChartTooltip: React.FC<{
  anchor: TooltipAnchor | null;
  children: React.ReactNode;
  zIndex?: number;
  // Clamp the tooltip within this element instead of the viewport, so a
  // left-edge tooltip stops at the chart edge rather than sliding under the
  // app nav rail.
  boundary?: Element | null;
}> = ({anchor, children, zIndex = 100, boundary}) => {
  const placement: Placement = anchor?.placement === 'right' ? 'right' : 'top';

  const {refs, floatingStyles} = useFloating({
    placement,
    strategy: 'fixed',
    middleware: [
      offset(ANCHOR_OFFSET),
      flip({padding: VIEWPORT_MARGIN}),
      shift({padding: VIEWPORT_MARGIN, boundary: boundary ?? undefined}),
    ],
    whileElementsMounted: autoUpdate,
  });

  useLayoutEffect(() => {
    if (!anchor) {
      return;
    }
    refs.setReference({
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        x: anchor.x,
        y: anchor.y,
        top: anchor.y,
        bottom: anchor.y,
        left: anchor.x,
        right: anchor.x,
      }),
    });
  }, [anchor, refs]);

  if (!anchor || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className="tw-style">
      <div
        ref={refs.setFloating}
        className="pointer-events-none min-w-[120px] max-w-[260px] rounded-md border border-moon-700/60 bg-moon-900 px-10 py-7 text-white shadow-[0_10px_24px_rgba(0,0,0,0.22),0_2px_6px_rgba(0,0,0,0.18)] dark:border-moon-700/60 dark:bg-moon-900 dark:text-white"
        style={{...floatingStyles, zIndex}}>
        {children}
      </div>
    </div>,
    document.body
  );
};

export const TooltipTitle: React.FC<{children: React.ReactNode}> = ({
  children,
}) => (
  <div className="mb-5 truncate text-[11px] font-semibold tracking-wide text-white">
    {children}
  </div>
);

export const TooltipRow: React.FC<{
  colorClass?: string; // Tailwind class e.g. "bg-red-500"
  label: React.ReactNode;
  value?: React.ReactNode;
  emphasized?: boolean;
}> = ({colorClass, label, value, emphasized}) => (
  <div
    className={`flex items-center gap-8 text-[11px] ${
      emphasized ? 'font-medium text-white' : 'text-wb-text-tertiary'
    }`}>
    {colorClass !== undefined && (
      <span className={`h-7 w-7 shrink-0 rounded-sm ${colorClass}`} />
    )}
    <span className="min-w-0 flex-1 truncate">{label}</span>
    {value !== undefined && (
      <span className="font-mono text-white">{value}</span>
    )}
  </div>
);

export const anchorAboveRect = (rect: DOMRect): TooltipAnchor => ({
  x: rect.left + rect.width / 2,
  y: rect.top,
  placement: 'top',
});
